import React, { useState } from "react";
import ThemeProvider from "@onnit-js/ui/components/themes/ThemeProvider";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import theme from "@onnit-js/ui/components/themes/light";
import Box from "@onnit-js/ui/components/box/Box";
import { Text } from "@onnit-js/ui/components/text/Text";
import { Img, Picture } from "@onnit-js/ui/components/image";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import ImageContentPortrait from "@onnit-js/ui/components/module/ImageContentPortrait";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import { Sup } from "@onnit-js/ui/components/text";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import Product from "@onnit-js/ui/@types/interfaces/product/BuyBoxProduct";
import SimpleBuyBox from "@onnit-js/ui/components/buybox/simple/SimpleBuyBox";
import ImgDualPortrait from "@onnit-js/ui/components/module/ImgDualPortrait";
import { Testimonial, TestimonialList } from "@onnit-js/ui/components/module/testimonials";
import config from "../../config/config";
import SupplementFacts from "../../components/common/SupplementFacts";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import usePageData from "../../hooks/usePageData";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";

const transformProductName = (name: string): string => name.replace(/Grass Fed Whey Isolate Protein - /giu, "").replace(/\(30 Serving Tub\)/giu, "");

export default function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) {
        return null;
    }

    const IMGIX_URL = `${onnitContext.url.cdn.static_images}/product-page/whey-protein`;
    const IMGIX_PARAMS = config.IMG_PARAMS;

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <DefaultProductPageTop
                    productNameTransform={transformProductName}
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid variant="layout3">
                        <Img
                            src={`${IMGIX_URL}/2023-WheyP-wpage-gallery-1.png${IMGIX_PARAMS}`}
                            lazyLoad={false}
                            alt="Whey isolate protein grass fed 30 capsules mexican chocolate"
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMGIX_URL}/2023-WheyP-wpage-gallery-2.png${IMGIX_PARAMS}`}
                            lazyLoad={false}
                            alt="Whey isolate protein grass fed 30 capsules vailnlla flavored"
                            width={920}
                            height={920}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMGIX_URL}/2023-WheyP-wpage-gallery-3-mobile.png${IMGIX_PARAMS}`,
                                `${IMGIX_URL}/2023-WheyP-wpage-gallery-3.png${IMGIX_PARAMS}`,
                            ]}
                            alt="Athlete with Onnit Whey Protein"
                        />
                        <Img
                            src={`${IMGIX_URL}/2023-WheyP-wpage-gallery-4.png${IMGIX_PARAMS}`}
                            lazyLoad={false}
                            alt="Woman exercising on an elliptical"
                            width={928}
                            height={938}
                        />
                        <Img
                            src={`${IMGIX_URL}/2023-WheyP-wpage-gallery-5.png${IMGIX_PARAMS}`}
                            lazyLoad={false}
                            alt="Athlete recovering after workout with whey protein shake"
                            width={920}
                            height={920}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                {/* Hide body of page on IE */}
                <div className="hide-ie">
                    <Box bg="black" py={[6, 6, 6, 8]}>
                        <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                            <Box gridColumn="2 / span 7" px={4}>
                                <Text as="h2" color="grays.3" typeStyle="text02">
                                    Grass-Fed Whey Isolate Protein
                                </Text>
                            </Box>
                        </TwelveColumn>
                        <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                            <Box gridColumn="7 / span 5" px={4}>
                                <Img
                                    src={`${IMGIX_URL}/2023-WheyP-wpage-intro.png?q=75&auto=format`}
                                    alt="Athlete working out"
                                    width={592}
                                    height={804}
                                />
                            </Box>
                            <Box
                                gridColumn="2 / span 5"
                                display={["block", "block", "flex"]}
                                alignItems="center"
                                px={4}
                            >
                                <Text
                                    display={["block", "block", "none"]}
                                    as="h2"
                                    color="grays.3"
                                    typeStyle="text02"
                                    textTransform="uppercase"
                                    my={3}
                                >
                                    Grass-Fed Whey Isolate Protein
                                </Text>

                                <Text as="h2" color="onGray" typeStyle="title01" mb={2}>
                                    Supports Muscle Gains
                                    <Sup style={{ fontSize: 24, verticalAlign: "top", color: "onGray" }} />
                                </Text>
                            </Box>
                        </TwelveColumn>

                        <TwelveColumn display={["block", "block", "grid"]}>
                            <Box gridColumn="2 / span 6" px={4} mb={[6, 6, 6, 8]}>
                                <Text as="p" typeStyle="text02" color="onGray">
                                    Unlike whey protein concentrate, whey isolate has had most of the fat and carbs
                                    from its milk source filtered out, leaving you with what you want—protein to help
                                    you build muscle, recover from workouts, and support lean body mass.
                                    <Sup style={{ fontSize: 12, verticalAlign: "top", color: "onGray" }} />
                                </Text>
                            </Box>
                            <Box gridColumn="3 / span 7" px={4}>
                                <Text as="h2" color="onGray" typeStyle="title01" mb={2}>
                                    Promotes Workout Recovery <Sup
                                        style={{ fontSize: 24, verticalAlign: "top", color: "onGray" }}
                                    />
                                </Text>
                                <Text as="p" typeStyle="text02" color="onGray">
                                    Whey is a supplement superstar in part because of its high leucine content. Leucine
                                    is a powerful
                                    branched-chain amino acid that signals the body to activate protein synthesis. The
                                    sooner you
                                    get protein (and the leucine within it) into your muscles, the sooner you can begin
                                    to recover
                                    from your workout. Due to its fast absorption, whey protein supports muscle recovery
                                    in hard
                                    training athletes and gym goers who partake in multiple activities per day.
                                    <Sup style={{ fontSize: 12, verticalAlign: "top", color: "onGray" }} />
                                </Text>
                            </Box>
                        </TwelveColumn>
                    </Box>

                    <Box py={[6, 6, 6, 8]}>
                        <Text typeStyle="title01" textAlign="center" mb={4}>
                            Available in two flavors
                        </Text>
                        <Box display="flex" justifyContent="center">
                            {pageData.products.map((product: Product, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Box maxWidth="364px" px={[4, 4, 6]} key={index}>
                                    <SimpleBuyBox
                                        product={product}
                                        productNameTransform={transformProductName}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box py={[6, 6, 6, 8]}>
                        <ImageContentPortrait
                            mb={[6, 6, 8]}
                            reverse
                            image={(
                                <Img
                                    src={`${IMGIX_URL}/2023-WheyP-wpage-Information-cows.png${IMGIX_PARAMS}`}
                                    alt="cows in a field"
                                    width={930}
                                    height={1200}
                                />
                            )}
                            content={(
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    height="100%"
                                    justifyContent="center"
                                    py={[0, 0, 6, 8]}
                                >
                                    <Text as="h2" color="black" typeStyle="title02" mb={4}>
                                        Grass-Fed Cows=Happy Cows
                                    </Text>
                                    <Text as="p" fontSize={[2, 2, 3]} color="black" lineHeight={6} mb={4}>
                                        Onnit's Whey Protein is an easily digestible and highly absorbable protein
                                        supplement.
                                        It features non-GMO whey protein isolate derived from the milk of Irish dairy
                                        cows that have met Animal Welfare Approved standards. That means the cows were
                                        raised on farms where they can graze an average of 250 days per year.**
                                    </Text>
                                    <Text typeStyle="caption02" color="grays.5" mb={4}>
                                        ** Based on a 3 year average; Certified Animal Welfare Approved (AWA) by A
                                        Greener World
                                    </Text>
                                </Box>
                            )}
                        />
                    </Box>

                    <Box backgroundColor="white" py={[4, 4, 4, 8]} px={4}>
                        <TestimonialList height={[360, 200, 260, 300]}>
                            <Testimonial
                                imgSrc={`${IMGIX_URL}/2023-WheyP-Wpage-testimonial-aubrey-fix.png${IMGIX_PARAMS}`}
                                quote={`"We took another run at coming up with the perfect grass-fed whey protein supplement. This time we nailed it. It is the right size, the right quality, the right taste, and unequivocally one of the best proteins on the market."`}
                                author="Aubrey Marcus"
                                title="Onnit Founder"
                            />
                            <Testimonial
                                imgSrc={`${IMGIX_URL}/2023-WheyP-Wpage-testimonial-neen-fix.png${IMGIX_PARAMS}`}
                                quote={`"After an intense workout, I use Onnit Whey Protein powder to help aid recovery. I'll also mix it with a few other supplements for a quick protein boost throughout the day.†"`}
                                author="Neen Williams"
                                title="Pro Skateboarder"
                            />
                        </TestimonialList>
                    </Box>

                    <ImgDualPortrait mb={4}>
                        <Img
                            src={`${IMGIX_URL}/2023-WheyP-wpage-lifestyleCollage01-1.png${IMGIX_PARAMS}`}
                            alt="Athlete working out"
                            width={930}
                            height={930}
                        />
                        <Img
                            src={`${IMGIX_URL}/2023-WheyP-wpage-lifestyleCollage01-2.jpg${IMGIX_PARAMS}`}
                            alt="Athlete lifting medicine ball"
                            width={930}
                            height={1328}
                        />
                    </ImgDualPortrait>

                </div>

                {pageData.suggested_use && pageData.supplement_facts && (
                    <UsageAndDisclaimers supplementFacts={(
                        <SupplementFacts
                            productNameTransform={transformProductName}
                            currentPID={currentPID}
                        />
                    )}
                    >
                        <Text typeStyle="caption02" color="grays.5" mb={4}>
                            <b>WARNING:</b> Consult a medical doctor before taking this or any other nutritional
                            supplement if you are pregnant, nursing, have or suspect a medical condition, or are
                            taking any medications. Store in a cool, dry place. <b>KEEP OUT OF REACH OF CHILDREN.</b>
                        </Text>
                    </UsageAndDisclaimers>
                )}
            </>
        </ThemeProvider>
    );
}
